import { Component, OnInit, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { formatDate } from '@angular/common';

import { FormFromEventComponent } from 'src/app/elements/form-component';
import { IEvent, ISeatingPlanEventChannel } from 'src/app/models/event.model';
import { Invitations } from 'src/app/models/invitation.model';
import { ITicketType, ITicketTypeCategory } from 'src/app/models/ticket.model';
import { ISession } from 'src/app/models/session.model';
import { GetParams } from 'src/app/models/type.definition';
import { translatedOrigin } from 'src/app/models/order.model';
import {
  AuthService,
  EventService,
  EventSessionService,
  InvitationService,
  TicketTypeService
} from 'src/app/providers';
import { GetEvent } from '../../getEvent';
import jspreadsheet from 'jspreadsheet-ce';

declare var seatsio: any;

interface TicketTypeGroup {
name: string;
tickets: ITicketType[];
}

interface ComponentState {
isLoading: boolean;
spreadsheetOk: boolean;
qtyOk: boolean;
currentStock?: number;
}

interface SpreadsheetRow {
firstName: string;
lastName: string;
email: string;
quantity?: number;
seat?: string;
}

@Component({
selector: 'app-event-orders-create',
templateUrl: './event-orders-create.component.html',
styleUrls: ['./event-orders-create.component.scss'],
providers: [GetEvent]
})
export class EventOrdersCreateComponent extends FormFromEventComponent<Invitations> implements OnInit, OnDestroy, AfterViewInit {
@ViewChild("spreadsheet") spreadsheet: ElementRef<HTMLDivElement>;

// Public properties for template
public readonly dataName = 'Invitation';
public readonly EMAIL_PATTERN = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

public event: IEvent;
public ticketTypes: TicketTypeGroup[] = [];
public eventSessions: ISession[] | null = null;
public seatsIoChannels: ISeatingPlanEventChannel[] = [];
public isAdmin: boolean;
public savedEmailContent: string | null = null;

ngAfterViewInit(): void {
  // S'assurer que la vue est initialisée
  setTimeout(() => {
    this.initTable();
    this.cdr.detectChanges();
  });
}

// Component state
private readonly state$ = new BehaviorSubject<ComponentState>({
  isLoading: false,
  spreadsheetOk: false,
  qtyOk: true,
});

// Observable state for template (simples getters au lieu d'observables complexes)
public get isLoading(): boolean {
  return this.state$.value.isLoading;
}

public get spreadsheetOk(): boolean {
  return this.state$.value.spreadsheetOk;
}

public get qtyOk(): boolean {
  return this.state$.value.qtyOk;
}

private readonly destroy$ = new Subject<void>();
private readonly loadedTicketTypes: ITicketType[] = [];
private readonly selectedSeatsSubject$ = new BehaviorSubject<string[]>([]);
private readonly seatsButtonLabelSubject$ = new BehaviorSubject<string>("Mettre à jour le tableau");

// Internal component state
private table: any = null;
private chart: any = null;
private currentCategory: ITicketTypeCategory;
private eventId: string;
private eventType: "event" | "session" = "event";

protected readonly getEventParams: GetParams<IEvent> = {
  select: ['status', 'ticketing']
};

// Observable getters for template (simplifiés)
public get selectedSeats(): string[] {
  return this.selectedSeatsSubject$.value;
}

public get seatsButtonLabel(): string {
  return this.seatsButtonLabelSubject$.value;
}

public get isFormInvalid(): boolean {
  return this.mainForm?.invalid || false;
}

public get isFormValid(): boolean {
  return this.mainForm?.valid || false;
}

public get formStatus(): string {
  return this.mainForm?.status || 'NO_FORM';
}

public get formErrors(): any {
  if (!this.mainForm) return {};

  const errors: any = {};
  Object.keys(this.mainForm.controls).forEach(key => {
    const control = this.mainForm.get(key);
    if (control && control.errors) {
      errors[key] = control.errors;
    }
  });
  return errors;
}

constructor(
  protected formProvider: InvitationService,
  protected getEvent: GetEvent,
  protected ticketTypeService: TicketTypeService,
  protected eventSessionService: EventSessionService,
  protected route: ActivatedRoute,
  private eventService: EventService,
  private authService: AuthService,
  private cdr: ChangeDetectorRef
) {
  super();
}

async ngOnInit(): Promise<void> {
  this.updateLoadingState(true);

  try {
    await this.initializeRouteParams();
    this.isAdmin = this.authService.isAdmin;

    await super.ngOnInit();
    //await this.loadSeatingChannelsIfNeeded();
    this.cdr.detectChanges(); // S'assurer que l'interface est mise à jour

    this.eventType = this.event.dates.type === 'sessions' ? 'session' : 'event';

  } catch (error) {
    this.handleError('Erreur lors de l\'initialisation', error);
  } finally {
    this.updateLoadingState(false);
  }
}

ngOnDestroy(): void {
  this.destroy$.next();
  this.destroy$.complete();
  this.cleanupResources();
}

// ====================
// INITIALIZATION METHODS
// ====================

private async initializeRouteParams(): Promise<void> {
  return new Promise<void>(resolve => {
    this.route.params.pipe(
      takeUntil(this.destroy$)
    ).subscribe(params => {
      if (params.id) {
        this.eventId = params.id;
      }
      resolve();
    });
  });
}


async onInit(): Promise<void> {
  if (this.event.status === 'draft') {
    this.router.navigate(['../'], { relativeTo: this.route });
    return;
  }

  await this.loadTicketTypes();
  await this.loadEventSessionsIfNeeded();
}

private async loadTicketTypes(): Promise<void> {
  const params: GetParams<ITicketType> = {
    filter: { isProduct: false },
    select: ['name', 'stock', 'ticketTypeCategoryId'],
    populate: ['ticketTypeCategoryId'],
    perPage: 500
  };

  const ticketTypes = await this.getListFromEventOf(this.ticketTypeService, params);
  this.loadedTicketTypes.length = 0;
  this.loadedTicketTypes.push(...ticketTypes);

  this.ticketTypes = this.groupTicketTypesByCategory(ticketTypes);
}

private async loadEventSessionsIfNeeded(): Promise<void> {
  if (this.event.dates.type === "sessions") {
    this.eventSessions = await this.getListFromEventOf(this.eventSessionService);
  }
}

private groupTicketTypesByCategory(ticketTypes: ITicketType[]): TicketTypeGroup[] {
  const categories = [...new Set(ticketTypes.map(tt => tt.ticketTypeCategoryId?._id))];

  const result = categories.map(categoryId => {
    const categoryTickets = ticketTypes.filter(tt => tt.ticketTypeCategoryId?._id === categoryId);
    return {
      name: categoryTickets[0].ticketTypeCategoryId?.name || 'Sans Catégorie',
      tickets: categoryTickets
    };
  });

  this.cdr.detectChanges(); // Forcer la détection après mise à jour des données
  return result;
}

// ====================
// FORM INITIALIZATION
// ====================

initForm(): void {
  const orderType = this.route.snapshot.queryParams.orderType || 'invitation';
  this.mainForm = this.formBuilder.group({
    ticketTypeId: new FormControl("", Validators.required),
    sessionId: new FormControl(null),
    orderType: new FormControl(orderType),
    amount: new FormControl({ value: 1, disabled: false }),
    isPaid: new FormControl(false),
    sendEmail: new FormControl(false), // Auto-enable for NFT
    addPlacement: new FormControl(false),
    blockSeats: new FormControl(false),
    emailObject: new FormControl(null, this.requiredIfSendByEmail),
    emailContent: new FormControl(null, this.requiredWysiwygContent),
    includeCommission: new FormControl(false),
  });

  setTimeout(() => {
    this.setupFormSubscriptions();
    this.cdr.detectChanges();
  }, 0);

}

private setupFormSubscriptions(): void {
  if (!this.mainForm) {
    return;
  };
  // Ticket type changes - with debounce to prevent rapid API calls
  this.mainForm.get('ticketTypeId').valueChanges.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    takeUntil(this.destroy$)
  ).subscribe(ticketTypeId => this.handleTicketTypeChange(ticketTypeId));

  if (this.eventType === "session") {
    this.mainForm.get('sessionId').valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(sessionId => this.handleSessionChange(sessionId));
  }

  // Amount changes - immediate for UX responsiveness
  this.mainForm.get('amount').valueChanges.pipe(
    takeUntil(this.destroy$)
  ).subscribe(amount => this.handleAmountChange(amount));

  // Seating-related changes
  this.mainForm.get('blockSeats').valueChanges.pipe(
    takeUntil(this.destroy$)
  ).subscribe(blockSeats => this.handleBlockSeatsChange(blockSeats));

  this.mainForm.get('addPlacement').valueChanges.pipe(
    takeUntil(this.destroy$)
  ).subscribe(addPlacement => this.handleAddPlacementChange(addPlacement));

  // Email notification changes
  this.mainForm.get('sendEmail').valueChanges.pipe(
    takeUntil(this.destroy$)
  ).subscribe(sendEmail => {
    this.handleSendEmailChange(sendEmail);
    this.cdr.detectChanges();
  });
}

// ====================
// FORM CHANGE HANDLERS
// ====================

private handleTicketTypeChange(ticketTypeId: string): void {
  if (!ticketTypeId) return;

  this.calculateAndUpdateStock(ticketTypeId);

  const shouldShowSeatingPlan = this.shouldShowSeatingPlan();
  if (shouldShowSeatingPlan) {
    this.resetSeatingComponents();
    this.displaySeatsPlan();
  }
  if (!this.isPlacementMode() || this.event.bookType === 'nft') {
    this.initTable();
  }
  if (!this.event.useRoomPlacement && !this.table) {
    this.resetSpreadsheetTable();
    this.initTable();
  }
}

private handleSessionChange(sessionId: string): void {
  if (!sessionId) return;
  const shouldShowSeatingPlan = this.shouldShowSeatingPlan();
  if (shouldShowSeatingPlan) {
    this.resetSeatingComponents();
    this.displaySeatsPlan();
  }
  if (!this.isPlacementMode()) {
    this.initTable();
  }
  if (!this.event.useRoomPlacement && !this.table) {
    this.resetSpreadsheetTable();
    this.initTable();
  }
}

private handleAmountChange(amount: number): void {
  if (amount === null) return;

  const clampedAmount = this.clampAmount(amount);
  if (clampedAmount !== amount) {
    this.mainForm.patchValue({ amount: clampedAmount }, { emitEvent: false });
    return;
  }

  this.updateSpreadsheetRows(clampedAmount);
}

private handleBlockSeatsChange(blockSeats: boolean): void {
  if (blockSeats) {
    this.mainForm.get('amount').disable();
    this.resetSeatingComponents();
    this.displaySeatsPlan();
  } else {
    this.mainForm.get('amount').enable();
    this.cleanupChart();
    this.resetSpreadsheetTable();
    this.initTable();
  }
}

private handleAddPlacementChange(addPlacement: boolean): void {
  this.resetSpreadsheetTable();
  this.initTable();
}

private handleSendEmailChange(sendEmail: boolean): void {

  // Forcer la revalidation des champs email
  const emailObjectControl = this.mainForm.get('emailObject');
  const emailContentControl = this.mainForm.get('emailContent');

  // Sauvegarder la valeur actuelle avant que le composant soit détruit
  if (!sendEmail) {
    this.savedEmailContent = emailContentControl?.value;
  }

  if (emailObjectControl) {
    emailObjectControl.updateValueAndValidity();
  }

  if (emailContentControl) {
    emailContentControl.updateValueAndValidity();
  }

  this.cdr.detectChanges();
}

// ====================
// SPREADSHEET MANAGEMENT
// ====================

initTable(): void {
  if (this.table || !this.mainForm?.get('ticketTypeId').value) return;

  const config = this.createSpreadsheetConfig();
  this.table = jspreadsheet(this.spreadsheet.nativeElement, config);
}

private createSpreadsheetConfig(): any {
  const isPlacementMode = this.isPlacementMode();
  const selectedSeats = this.selectedSeatsSubject$.value;

  const baseConfig = {
    minDimensions: [4, 1],
    allowManualInsertColumn: false,
    allowManualInsertRow: false,
    columnResize: false,
    rowResize: false,
    ngInstance: this,
    contextMenu: () => false,
    onchange: (instance: any, cell: any, x: number, y: number, value: any) => {
      this.handleSpreadsheetChange(instance, cell, x, y, value);
    }
  };

  if (isPlacementMode) {
    return {
      ...baseConfig,
      data: selectedSeats.map(seat => ["", "", "", seat]),
      columns: [
        { title: "Prénom", type: "text", width: 190 },
        { title: "Nom", type: "text", width: 190 },
        { title: "Email", type: "text", width: 190 },
        {
          title: "Numéro de place",
          type: "text",
          width: 240,
          readOnly: this.mainForm.get('blockSeats').value
        },
      ]
    };
  } else {
    return {
      ...baseConfig,
      data: [[]],
      columns: [
        { title: "Prénom", type: "text", width: 190 },
        { title: "Nom", type: "text", width: 190 },
        { title: "Quantité", type: "numeric", mask: '0', width: 90 },
        { title: "Email", type: "text", width: 190 },
      ]
    };
  }
}

private updateSpreadsheetRows(targetAmount: number): void {
  if (!this.table) return;

  const currentRowCount = this.table.getData().length;

  if (targetAmount < currentRowCount) {
    this.removeExcessRows(currentRowCount, targetAmount);
  } else if (targetAmount > currentRowCount) {
    this.addMissingRows(targetAmount - currentRowCount);
  }
}

private removeExcessRows(currentCount: number, targetCount: number): void {
  for (let i = currentCount - 1; i >= targetCount; i--) {
    this.table.deleteRow();
  }
}

private addMissingRows(rowsToAdd: number): void {
  Array.from({ length: rowsToAdd }).forEach(() => this.table.insertRow());
}

private handleSpreadsheetChange(instance: any, cell: any, x: number, y: number, value: any): void {
  this.validateSpreadsheetData();

  if (x === 2) { // Quantity column
    this.validateQuantityConstraints();
  }
}

// ====================
// SEATING PLAN MANAGEMENT
// ====================

displaySeatsPlan(): void {
  const categoryId = this.getCurrentCategoryId();
  const eventId = (this.eventType === "session") ? this.mainForm.get('sessionId').value : this.eventId;
  this.subscription.add(
    this.eventService.getSeatingPlan(null, true, eventId, this.eventType)
      .subscribe(results => {
        const config = this.createSeatingPlanConfig(results, eventId, categoryId);
        this.chart = new seatsio.EventManager(config).render();
      })
  );
}

private getCurrentCategoryId(): string | null {
  const ticketTypeId = this.mainForm.get('ticketTypeId').value;
  const ticketType = this.loadedTicketTypes.find(tt => tt._id === ticketTypeId);

  if (ticketType?.ticketTypeCategoryId) {
    this.currentCategory = ticketType.ticketTypeCategoryId;
    return ticketType.ticketTypeCategoryId.seatsIoCategoryId;
  }

  return null;
}

private createSeatingPlanConfig(results: any, eventId: string, categoryId: string | null): any {
  return {
    divId: "chartRenderer",
    secretKey: results.workspaceSecretKey,
    event: eventId,
    mode: 'select',
    language: "fr",
    extraConfig: { categoryId },
    maxSelectedObjects: 25,
    isObjectSelectable: (object: any, defaultValue: any, extraConfig: any) => {
      return object.status === 'free' &&  Number(extraConfig.categoryId) === Number(object.category.key);
    },
    unavailableObjectsSelectable: false,
    objectPopover: {
      showOrderId: true,
      showTechnicalLabel: true
    },
    popoverInfo: (object: any) => this.createPopoverInfo(object),
    onObjectSelected: (object: any) => this.handleSeatSelected(object),
    onObjectDeselected: (object: any) => this.handleSeatDeselected(object)
  };
}

private createPopoverInfo(object: any): string | null {
  if (object.capacity || object.capacity === 0 || !object.extraData) {
    return null;
  }

  const username = `${object.extraData?.firstName || ''} ${object.extraData?.lastName || ''}`.trim();
  const email = object.extraData?.email || 'Non renseigné';
  const ticketNumber = object.extraData?.ticketNumber || 'Non renseigné';
  const date = object.extraData?.date
    ? formatDate(object.extraData.date, 'dd/MM/yyyy HH:mm', 'fr')
    : 'Non renseignée';
  const origin = object.extraData?.origin
    ? translatedOrigin(object.extraData.origin)
    : 'Non renseigné';

  return `N°ticket: [pre]${ticketNumber}[/pre][br/][br/]${username}[br/]${email}[br/]Date: ${date}[br/]Origine: ${origin}`;
}


private async handleSeatSelected(object: any): Promise<void> {
  await this.updateSpreadsheetFromCurrentSelection();
}

private async handleSeatDeselected(object: any): Promise<void> {
  await this.updateSpreadsheetFromCurrentSelection();
}

private async updateSpreadsheetFromCurrentSelection(): Promise<void> {
  if (!this.chart) return;

  try {
    const selectedObjects = await this.chart.listSelectedObjects();
    const seatIds = selectedObjects.map((obj: any) => obj.id);

    this.mainForm.patchValue({ amount: seatIds.length });
    this.selectedSeatsSubject$.next(seatIds);
    this.updateSpreadsheetWithSeats(seatIds);
  } catch (error) {
    console.error('Erreur lors de la mise à jour du tableau:', error);
  }
}

private updateSpreadsheetWithSeats(seatIds: string[]): void {

  if (!this.isPlacementMode()) return;

  // Sauvegarder les données existantes indexées par siège
  const existingData = this.preserveExistingUserData();

  // Réinitialiser le tableau avec les nouveaux sièges
  this.resetSpreadsheetTable();
  this.selectedSeatsSubject$.next(seatIds);
  this.initTable();

  // Restaurer les données utilisateur pour les sièges qui sont toujours sélectionnés
  this.restoreUserData(existingData);
}

private preserveExistingUserData(): Map<string, any> {
  const existingData = new Map<string, any>();

  if (!this.table) return existingData;

  const currentData = this.table.getData();
  currentData.forEach((row: any[]) => {
    const seatId = row[3]; // La colonne siège
    if (seatId && (row[0] || row[1] || row[2])) { // Si le siège existe et qu'il y a des données utilisateur
      existingData.set(seatId, {
        firstName: row[0] || '',
        lastName: row[1] || '',
        email: row[2] || ''
      });
    }
  });

  return existingData;
}

private restoreUserData(existingData: Map<string, any>): void {
  if (!this.table || existingData.size === 0) return;

  const currentData = this.table.getData();
  let hasChanges = false;

  currentData.forEach((row: any[], index: number) => {
    const seatId = row[3];
    if (seatId && existingData.has(seatId)) {
      const userData = existingData.get(seatId);

      // Mettre à jour seulement si les données sont différentes
      if (row[0] !== userData.firstName ||
          row[1] !== userData.lastName ||
          row[2] !== userData.email) {

        this.table.setValueFromCoords(0, index, userData.firstName, true);
        this.table.setValueFromCoords(1, index, userData.lastName, true);
        this.table.setValueFromCoords(2, index, userData.email, true);
        hasChanges = true;
      }
    }
  });

  // Déclencher la validation si des données ont été restaurées
  if (hasChanges) {
    this.validateSpreadsheetData();
  }
}

// ====================
// VALIDATION METHODS
// ====================

private validateSpreadsheetData(): void {
  if (!this.table) return;

  const validRows = this.getValidSpreadsheetRows();
  const hasValidData = validRows.length > 0;

  let allRowsValid = true;

  validRows.forEach((row, index) => {
    const rowNumber = index + 1;
    this.resetRowStyles(rowNumber);

    if (!this.isRowComplete(row)) {
      allRowsValid = false;
    } else {
      const emailColumnIndex = this.isPlacementMode() ? 2 : 3;
      if (!this.validateEmail(row[emailColumnIndex])) {
        this.markEmailAsInvalid(rowNumber, emailColumnIndex);
        allRowsValid = false;
      }
    }
  });

  this.updateState({
    spreadsheetOk: hasValidData && allRowsValid
  });
}

private validateQuantityConstraints(): void {
  if (!this.table) return;

  const totalQuantity = this.calculateTotalQuantity();
  const currentStock = this.state$.value.currentStock;

  this.updateState({
    qtyOk: currentStock === undefined || totalQuantity <= currentStock
  });
}

private calculateTotalQuantity(): number {
  const rows = this.table.getData();

  return rows.reduce((sum: number, row: any[]) => {
    if (this.isPlacementMode()) {
      return sum + 1; // Each row represents one seat
    } else {
      const quantity = row[2] ? parseInt(row[2], 10) : 0;
      return sum + (isNaN(quantity) ? 0 : quantity);
    }
  }, 0);
}

private validateEmail(email: string): boolean {
  return this.EMAIL_PATTERN.test(email);
}

private requiredWysiwygContent(control: AbstractControl): ValidationErrors | null {
  if (!control.parent) return null;

  const sendEmail = control.parent.get('sendEmail')?.value;

  if (!sendEmail) {
    return null; // Pas requis si sendEmail est false
  }

  const value = control.value;

  // Vérifier si la valeur est vide ou contient seulement des balises vides
  if (!value) {
    return { required: true };
  }

  // Nettoyer le HTML pour vérifier s'il contient du vrai contenu
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = value;
  const textContent = tempDiv.textContent || tempDiv.innerText || '';

  // Si après nettoyage il n'y a pas de texte, c'est vide
  if (textContent.trim().length === 0) {
    return { required: true };
  }

  return null;
}

private requiredIfSendByEmail(control: AbstractControl): ValidationErrors | null {
  if (!control.parent) return null;

  const sendEmail = control.parent.get('sendEmail')?.value;

  if (sendEmail && !control.value) {
    return { required: true };
  }

  return null;
}

// ====================
// BUSINESS LOGIC METHODS
// ====================

private calculateAndUpdateStock(ticketTypeId: string): void {
  const stock = this.calculateAvailableStock(ticketTypeId);
  this.updateState({ currentStock: stock });
  this.validateQuantityConstraints();
}

private calculateAvailableStock(ticketTypeId: string): number | undefined {
  const category = this.ticketTypes.find(cat =>
    cat.tickets.some(ticket => ticket._id === ticketTypeId)
  );

  if (!category) return undefined;

  const ticket = category.tickets.find(t => t._id === ticketTypeId);
  if (!ticket) return undefined;

  let availableStock: number | undefined;

  // Check ticket-level quota
  if (ticket.quota && ticket.stock !== undefined) {
    availableStock = ticket.stock;
  }

  // Check category-level quota
  const categoryStock = ticket.ticketTypeCategoryId?.stock;
  if (ticket.ticketTypeCategoryId?.quota && categoryStock !== undefined) {
    availableStock = availableStock !== undefined
      ? Math.min(availableStock, categoryStock)
      : categoryStock;
  }

  // Check event-level quota
  const eventStock = this.event.ticketing?.stock;
  if (this.event.ticketing?.quota && eventStock !== undefined) {
    availableStock = availableStock !== undefined
      ? Math.min(availableStock, eventStock)
      : eventStock;
  }

  return availableStock;
}

// ====================
// FORM SUBMISSION
// ====================

submitForm(): void {
  if (!this.table) return;

  const formValue = this.mainForm.value;
  const tickets = this.buildTicketsFromSpreadsheet();

  if (tickets.length === 0) {
    this.handleError('Aucun ticket valide trouvé');
    return;
  }

  const orderData = {
    ...formValue,
    tickets
  };

  delete orderData.amount;

  this.subscription.add(
    this.formProvider.generate(orderData, this.event._id).subscribe({
      next: () => {
        const pluralSuffix = tickets.length > 1 ? 's' : '';
        this.notificationService.newNotification({
          state: 'success',
          message: `Commande${pluralSuffix} envoyée${pluralSuffix} avec succès`
        });
        this.goBack();
      },
      error: (error) => this.handleError('Erreur lors de la création de la commande', error)
    })
  );
}

private buildTicketsFromSpreadsheet(): any[] {
  const rows = this.getValidSpreadsheetRows();

  return rows.map(row => {
    if (this.isPlacementMode()) {
      return {
        firstName: row[0],
        lastName: row[1],
        email: row[2],
        qty: 1,
        seat: [row[3]]
      };
    } else {
      return {
        firstName: row[0],
        lastName: row[1],
        qty: Number(row[2]) || 1,
        email: row[3]
      };
    }
  });
}

// ====================
// UTILITY METHODS
// ====================

public setEmailContentValue(emailContent: string): void {

  this.mainForm.patchValue({ emailContent });

  // Forcer la validation du contrôle
  const emailContentControl = this.mainForm.get('emailContent');
  if (emailContentControl) {
    emailContentControl.markAsTouched();
    emailContentControl.updateValueAndValidity();
  }

  this.cdr.detectChanges();
}

public toggleEmail(): void {
  // This method seems to be used in template, keeping for compatibility
}

public goBack(): void {
  this.router.navigate([`evenements/${this.event._id}/commandes/`]);
}

// ====================
// PRIVATE HELPER METHODS
// ====================

private shouldShowSeatingPlan(): boolean {
  return this.mainForm.get('addPlacement').value || this.mainForm.get('blockSeats').value;
}

private isPlacementMode(): boolean {
  return this.mainForm.get('addPlacement').value;
}

private clampAmount(amount: number): number {
  if (amount > 100) return 100;
  if (amount < 1) return 1;
  return amount;
}

private getValidSpreadsheetRows(): any[] {
  if (!this.table) return [];

  return this.table.getData().filter((row: any[]) => {
    return !(row[0] === '' && row[1] === '' && row[3] === '');
  });
}

private isRowComplete(row: any[]): boolean {
  if (this.isPlacementMode()) {
    return row[0] && row[1] && row[2] && row[3]; // firstName, lastName, email, seat
  } else {
    return row[0] && row[1] && row[2] && row[3]; // firstName, lastName, quantity, email
  }
}

private resetRowStyles(rowNumber: number): void {
  const emailColumn = this.isPlacementMode() ? 'C' : 'D';
  this.table.setStyle(`${emailColumn}${rowNumber}`, 'color', 'black');
}

private markEmailAsInvalid(rowNumber: number, columnIndex: number): void {
  const column = columnIndex === 2 ? 'C' : 'D';
  this.table.setStyle(`${column}${rowNumber}`, 'color', 'red');
}

private updateState(partialState: Partial<ComponentState>): void {
  this.state$.next({ ...this.state$.value, ...partialState });
  this.cdr.detectChanges();
}

private updateLoadingState(isLoading: boolean): void {
  this.updateState({ isLoading });
}

private resetSeatingComponents(): void {
  this.cleanupChart();
  this.resetSpreadsheetTable();
  this.selectedSeatsSubject$.next([]);
}

private resetSpreadsheetTable(): void {
  if (this.table) {
    jspreadsheet.destroy(this.spreadsheet.nativeElement);
    this.table = null;
  }
}

private cleanupChart(): void {
  if (this.chart) {
    this.chart.destroy();
    this.chart = null;
  }
}

private cleanupResources(): void {
  this.resetSpreadsheetTable();
  this.cleanupChart();
}

private handleError(message: string, error?: any): void {
  console.error(message, error);
  this.notificationService.newNotification({
    state: 'error',
    message: error?.message || message
  });
}
}